import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDate, formatDateTime } from "@/lib/utils";
import { Users, Plus, X, ShieldCheck, AlertCircle, Filter, History, RefreshCw, Database } from "lucide-react";

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  ADMIN_OPS: "Admin OPS",
  ADMIN_SALES: "Commercial",
  ADMIN_SUPPORT: "Admin Support",
};

const ROLE_VARIANT: Record<string, "default" | "secondary" | "outline" | "warning"> = {
  SUPER_ADMIN: "default",
  ADMIN_OPS: "secondary",
  ADMIN_SALES: "warning",
  ADMIN_SUPPORT: "outline",
};

// ─── Create Staff Modal ────────────────────────────────────────────────────────

function CreateStaffModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [staffRole, setStaffRole] = useState<"SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SALES" | "ADMIN_SUPPORT">("ADMIN_OPS");
  const [error, setError] = useState<string | null>(null);

  const create = trpc.admin.staff.create.useMutation({
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    create.mutate({ email, password, firstName: firstName || undefined, lastName: lastName || undefined, staffRole });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Créer un compte staff</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">Prénom</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Marie"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Nom</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Dupont"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="marie@constrigo.fr"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Mot de passe *</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 caractères"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role">Rôle *</Label>
            <select
              id="role"
              value={staffRole}
              onChange={(e) => setStaffRole(e.target.value as any)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="ADMIN_OPS">Admin OPS</option>
              <option value="ADMIN_SALES">Commercial</option>
              <option value="ADMIN_SUPPORT">Admin Support</option>
              <option value="SUPER_ADMIN">Super Admin</option>
            </select>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" className="flex-1" disabled={create.isPending}>
              {create.isPending ? "Création…" : "Créer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function StaffManagement() {
  const [showCreate, setShowCreate] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [syncResult, setSyncResult] = useState<any>(null);
  const utils = trpc.useUtils();

  const { data: staff, isLoading } = trpc.admin.staff.list.useQuery();

  const { data: auditLogs } = trpc.admin.audit.list.useQuery({
    page: 1,
    limit: 20,
    action: "STAFF",
  } as any);

  const updateRole = trpc.admin.staff.updateRole.useMutation({
    onSuccess: () => {
      utils.admin.staff.list.invalidate();
      utils.admin.audit.list.invalidate();
    },
  });

  const deactivate = trpc.admin.staff.deactivate.useMutation({
    onSuccess: () => {
      utils.admin.staff.list.invalidate();
      utils.admin.audit.list.invalidate();
    },
  });

  const syncSupabase = trpc.admin.staff.syncSupabaseUsers.useMutation({
    onSuccess: (data) => {
      setSyncResult(data);
      utils.admin.staff.list.invalidate();
    },
  });

  const handleRefresh = () => {
    utils.admin.staff.list.invalidate();
    utils.admin.audit.list.invalidate();
  };

  const filteredStaff = staff?.filter((s) => {
    if (!roleFilter) return true;
    return s.role === roleFilter;
  });

  const activeCount = staff?.filter((s) => s.isActive).length ?? 0;
  const inactiveCount = staff?.filter((s) => !s.isActive).length ?? 0;

  return (
    <AdminLayout>
      {showCreate && (
        <CreateStaffModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleRefresh}
        />
      )}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Staff</h1>
            <p className="text-muted-foreground">Gestion des comptes staff internes Constrigo</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => syncSupabase.mutate({} as any)}
              disabled={syncSupabase.isPending}
            >
              <Database className="h-4 w-4 mr-2" />
              {syncSupabase.isPending ? "Sync…" : "Sync Supabase"}
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau staff
            </Button>
          </div>
        </div>

        {syncResult && (
          <Card className="border-primary/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Database className="h-4 w-4" />
                Résultat sync Supabase
              </CardTitle>
              <button onClick={() => setSyncResult(null)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-muted-foreground">Auto-liés : </span>
                  <span className="font-semibold text-green-600">{syncResult.autoLinked ?? 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Orphelins Supabase : </span>
                  <span className="font-semibold text-warning">{syncResult.orphanedSupabase?.length ?? 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Sans lien Supabase : </span>
                  <span className="font-semibold text-destructive">{syncResult.missingSupabaseLink?.length ?? 0}</span>
                </div>
              </div>
              {syncResult.orphanedSupabase?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Orphelins Supabase (pas de compte local) :</div>
                  <div className="space-y-1">
                    {syncResult.orphanedSupabase.slice(0, 10).map((u: any) => (
                      <div key={u.id || u.email} className="text-xs text-muted-foreground">
                        {u.email || u.id}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {syncResult.missingSupabaseLink?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Utilisateurs locaux sans lien Supabase :</div>
                  <div className="space-y-1">
                    {syncResult.missingSupabaseLink.slice(0, 10).map((u: any) => (
                      <div key={u.id || u.email} className="text-xs text-muted-foreground">
                        {u.email || u.id}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {syncSupabase.isError && (
          <div className="flex items-center gap-2 text-sm text-destructive p-3 border border-destructive/20 rounded-lg">
            <AlertCircle className="h-4 w-4" />
            {syncSupabase.error?.message ?? "Erreur lors de la sync Supabase"}
          </div>
        )}

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total staff</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{isLoading ? "—" : staff?.length ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Actifs</CardTitle>
              <ShieldCheck className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {isLoading ? "—" : activeCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inactifs</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "—" : inactiveCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-10 pl-9 pr-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none cursor-pointer"
            >
              <option value="">Tous les rôles</option>
              <option value="ADMIN_OPS">Admin OPS</option>
              <option value="ADMIN_SALES">Commercial</option>
              <option value="ADMIN_SUPPORT">Admin Support</option>
              <option value="SUPER_ADMIN">Super Admin</option>
            </select>
          </div>
          {roleFilter && (
            <button
              onClick={() => setRoleFilter("")}
              className="h-10 px-3 rounded-lg border border-input bg-background text-sm text-muted-foreground hover:bg-secondary transition-colors"
            >
              Effacer
            </button>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium text-muted-foreground">Utilisateur</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Rôle</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Créé le</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Dernière activité</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-4"><Skeleton className="h-4 w-40" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-24" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-16" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-24" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-24" /></td>
                        <td className="p-4"><Skeleton className="h-8 w-32" /></td>
                      </tr>
                    ))
                  ) : !filteredStaff || filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        {roleFilter ? "Aucun staff avec ce rôle" : "Aucun compte staff"}
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((s) => (
                      <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          <div className="font-medium">
                            {[s.firstName, s.lastName].filter(Boolean).join(" ") || "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">{s.email}</div>
                        </td>
                        <td className="p-4">
                          <select
                            value={s.role ?? ""}
                            onChange={(e) => {
                              const newRole = e.target.value as "SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SALES" | "ADMIN_SUPPORT";
                              if (newRole !== s.role) {
                                updateRole.mutate({ userId: s.id, staffRole: newRole });
                              }
                            }}
                            disabled={updateRole.isPending}
                            className="h-7 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                          >
                            <option value="ADMIN_OPS">Admin OPS</option>
                            <option value="ADMIN_SALES">Commercial</option>
                            <option value="ADMIN_SUPPORT">Admin Support</option>
                            <option value="SUPER_ADMIN">Super Admin</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <Badge variant={s.isActive ? "success" : "secondary"}>
                            {s.isActive ? "Actif" : "Inactif"}
                          </Badge>
                        </td>
                        <td className="p-4 text-muted-foreground">{formatDate(s.createdAt)}</td>
                        <td className="p-4 text-muted-foreground">{formatDate(s.updatedAt)}</td>
                        <td className="p-4">
                          <Button
                            variant={s.isActive ? "outline" : "secondary"}
                            size="sm"
                            disabled={deactivate.isPending}
                            onClick={() => deactivate.mutate({ userId: s.id })}
                          >
                            {s.isActive ? "Désactiver" : "Réactiver"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent staff audit log */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4 text-primary" />
              Historique des changements staff
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Action</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Acteur</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditLogs as any)?.items?.length > 0 ? (
                    (auditLogs as any).items.map((log: any) => {
                      const actionLabel = {
                        STAFF_CREATE: "Création",
                        STAFF_UPDATE_ROLE: "Changement rôle",
                        STAFF_DEACTIVATE: "Désactivation",
                        STAFF_REACTIVATE: "Réactivation",
                      }[log.action as string] ?? log.action;

                      const actionVariant: any = {
                        STAFF_CREATE: "success",
                        STAFF_UPDATE_ROLE: "warning",
                        STAFF_DEACTIVATE: "destructive",
                        STAFF_REACTIVATE: "success",
                      }[log.action as string] ?? "secondary";

                      const details = log.after
                        ? `${log.before?.role ?? "—"} → ${log.after?.role ?? log.after?.isStaff !== undefined ? (log.after.isStaff ? "Réactivé" : "Désactivé") : "—"}`
                        : "—";

                      return (
                        <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="p-4 text-muted-foreground">{formatDateTime(log.createdAt)}</td>
                          <td className="p-4">
                            <Badge variant={actionVariant}>{actionLabel}</Badge>
                          </td>
                          <td className="p-4 text-muted-foreground">{log.actorEmail ?? log.actorId ?? "—"}</td>
                          <td className="p-4 text-muted-foreground text-xs">{details}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">
                        Aucun historique récent
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
